import { Loader2, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import {
  useCaseEntityTypes,
  useFindDuplicates,
} from '@/features/entity-resolution/hooks/useGroupReview'

interface FindDuplicatesModalProps {
  caseId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Maps a tolerance value to a human label — higher = stricter. */
function toleranceLabel(value: number): string {
  if (value >= 0.95) return 'almost exact'
  if (value >= 0.8) return 'strict'
  if (value >= 0.65) return 'moderate'
  return 'loose'
}

/**
 * On-demand duplicate search: pick an entity type and one of its properties from
 * the case schema, plus a fuzzy tolerance. Every set of same-type entities whose
 * value for that property clusters together becomes a hidden review group.
 * Nothing is auto-merged.
 */
export function FindDuplicatesModal({ caseId, open, onOpenChange }: FindDuplicatesModalProps) {
  const [label, setLabel] = useState('')
  const [property, setProperty] = useState('')
  const [threshold, setThreshold] = useState(0.85)
  const findDuplicates = useFindDuplicates(caseId)

  const { data: entityTypes = [], isLoading: schemaLoading } = useCaseEntityTypes(caseId, open)
  const selectedType = entityTypes.find(et => et.label === label)
  const properties = selectedType?.properties ?? []

  // Reset the form each time the modal opens.
  useEffect(() => {
    if (open) {
      setLabel('')
      setProperty('')
      setThreshold(0.85)
    }
  }, [open])

  const canRun = label.length > 0 && property.length > 0 && !findDuplicates.isPending

  const handleRun = () => {
    if (!canRun) return
    findDuplicates.mutate({ label, property, threshold }, { onSuccess: () => onOpenChange(false) })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Find more duplicates</DialogTitle>
          <DialogDescription>
            Look for entities of one type that share a property, and collect each match into a group
            to review. Nothing is merged automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label>Entity type</Label>
            <Select
              value={label}
              onValueChange={value => {
                setLabel(value)
                setProperty('')
              }}
              disabled={schemaLoading || entityTypes.length === 0}
            >
              <SelectTrigger className="max-md:w-full">
                <SelectValue
                  placeholder={schemaLoading ? 'Loading schema…' : 'Select an entity type'}
                />
              </SelectTrigger>
              <SelectContent>
                {entityTypes.map(et => (
                  <SelectItem key={et.label} value={et.label}>
                    {et.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!schemaLoading && entityTypes.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No schema entity types found for this case.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Property</Label>
            <Select value={property} onValueChange={setProperty} disabled={!selectedType}>
              <SelectTrigger className="max-md:w-full">
                <SelectValue
                  placeholder={selectedType ? 'Select a property' : 'Pick an entity type first'}
                />
              </SelectTrigger>
              <SelectContent>
                {properties.map(name => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Label>Tolerance</Label>
              <span className="text-sm text-muted-foreground">
                {threshold.toFixed(2)} · {toleranceLabel(threshold)}
              </span>
            </div>
            <Slider
              min={0.5}
              max={1}
              step={0.01}
              value={[threshold]}
              onValueChange={([v]) => setThreshold(v)}
            />
            <p className="text-xs text-muted-foreground">
              Lower finds more (typos, variants); higher is stricter (fewer false matches).
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleRun} disabled={!canRun}>
            {findDuplicates.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Search className="size-3.5" />
            )}
            Search
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
